import { computeProjections } from "./projections";

export const dash = (v) => (v != null ? v : "-");
export const withUnit = (v, unit) => (v != null ? `${v}${unit}` : "-");
export const toFixed3 = (v) => (v != null ? v.toFixed(3) : "-");

export const withProjections = (data) => {
  if (data.projLow != null && data.projExpected != null && data.projHigh != null) return data;
  return { ...data, ...computeProjections({ actualHR: data.actualHR, actionItems: data.actionItems }) };
};

export const computeScale = (values) => {
  const nums = values.filter((v) => v != null && Number.isFinite(v));
  if (nums.length === 0) return { min: 0, max: 1 };
  const lo = Math.min(...nums);
  const hi = Math.max(...nums);
  const spread = Math.max(hi - lo, 1);
  const pad = Math.max(spread * 0.18, 2);
  return { min: Math.max(0, Math.floor(lo - pad)), max: Math.ceil(hi + pad) };
};

export const makePct = (scale) => (v) => {
  const span = scale.max - scale.min;
  const clamped = Math.max(scale.min, Math.min(scale.max, v));
  return `${((clamped - scale.min) / span) * 100}%`;
};

export const projBarLabels = (s, formatCurrent) => [
  s.actualHR     != null && { at: s.actualHR,     text: formatCurrent(s.actualHR), sub: "current",  dim: true,  priority: 1 },
  s.projLow      != null && { at: s.projLow,      text: `${s.projLow}`,            sub: "floor",                priority: 2 },
  s.projHigh     != null && { at: s.projHigh,     text: `${s.projHigh}`,           sub: "ceiling",              priority: 2 },
  s.projExpected != null && { at: s.projExpected, text: `${s.projExpected}`,       sub: "expected", bold: true, priority: 3 },
].filter(Boolean);

/**
 * Assigns each projection-bar label a `pct` (0–100) and a `row` (0, 1, …).
 * Labels that sit on the exact same value collapse to the highest-priority
 * one (expected > floor/ceiling > current). Labels that would collide
 * horizontally drop to the next row instead of being shoved sideways.
 */
export const layoutLabels = (labels, scale, minGapPct = 9) => {
  const span = scale.max - scale.min;
  const toPct = (v) => ((Math.max(scale.min, Math.min(scale.max, v)) - scale.min) / span) * 100;

  const dominantByValue = new Map();
  for (const label of labels) {
    const existing = dominantByValue.get(label.at);
    if (!existing || (label.priority ?? 0) > (existing.priority ?? 0)) {
      dominantByValue.set(label.at, label);
    }
  }
  const merged = [...dominantByValue.values()].map((l, i) => ({ ...l, _i: i, pct: toPct(l.at) }));
  const byPosition = [...merged].sort((a, b) => a.pct - b.pct);

  const lastPctOnRow = [];
  for (const label of byPosition) {
    let row = 0;
    while (lastPctOnRow[row] != null && label.pct - lastPctOnRow[row] < minGapPct) row++;
    lastPctOnRow[row] = label.pct;
    label.row = row;
  }

  return byPosition
    .sort((a, b) => a._i - b._i)
    .map(({ _i, ...rest }) => rest);
};
